/**
 * 坐标转换纯函数 —— GCJ-02 ↔ WGS84/CGCS2000（天地图边界转换专用）
 *
 * 背景与边界（2026-08-28 任务7 R2 第一手调研落定）：
 *   - 全库历史坐标 GCJ-02（订单快照 + 高德 geocode 产出）——DB canonical，不迁移。
 *   - 天地图 = 国家平台，底图/服务按国家大地坐标系（CGCS2000≈WGS84，厘米级差异），
 *     官方 JS API 支持 EPSG:4326 直投，非 GCJ-02。
 *   - 故 Provider 边界必须转换：渲染前 GCJ02→WGS84；天地图 geocode/搜索产出落库前 WGS84→GCJ02。
 *   - 业务组件禁止 import 本文件——转换只发生在 services/map（provider/MapService）内部。
 *
 * 算法说明（诚实声明）：国标偏移算法未公开，此处为业界通行公开近似（克拉索夫斯基
 * 椭球 + 三角级数偏移），正向 wgs2gcj 与官方实现一致（米级内），逆向 gcj2wgsExact
 * 迭代收敛到亚米级。生产白名单生效后须以真实地点实测校验 marker 落点。
 */
const PI = 3.14159265358979324
const SEMI_AXIS = 6378245.0 // 克拉索夫斯基椭球长半轴
const ECC_SQ = 0.00669342162296594323 // 第一偏心率平方

// 中国大陆大致范围（含港澳）；范围外无偏移，恒等返回
export function outOfChina (lat, lng) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271
}

function transformLat (x, y) {
  let r = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x))
  r += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0
  r += (20.0 * Math.sin(y * PI) + 40.0 * Math.sin(y / 3.0 * PI)) * 2.0 / 3.0
  r += (160.0 * Math.sin(y / 12.0 * PI) + 320.0 * Math.sin(y * PI / 30.0)) * 2.0 / 3.0
  return r
}

function transformLng (x, y) {
  let r = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x))
  r += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0
  r += (20.0 * Math.sin(x * PI) + 40.0 * Math.sin(x / 3.0 * PI)) * 2.0 / 3.0
  r += (150.0 * Math.sin(x / 12.0 * PI) + 300.0 * Math.sin(x / 30.0 * PI)) * 2.0 / 3.0
  return r
}

// 在基准坐标上计算 GCJ-02 偏移量（度）
function delta (lat, lng) {
  let dLat = transformLat(lng - 105.0, lat - 35.0)
  let dLng = transformLng(lng - 105.0, lat - 35.0)
  const radLat = lat / 180.0 * PI
  let magic = Math.sin(radLat)
  magic = 1 - ECC_SQ * magic * magic
  const sqrtMagic = Math.sqrt(magic)
  dLat = (dLat * 180.0) / ((SEMI_AXIS * (1 - ECC_SQ)) / (magic * sqrtMagic) * PI)
  dLng = (dLng * 180.0) / (SEMI_AXIS / sqrtMagic * Math.cos(radLat) * PI)
  return { dLat, dLng }
}

/** WGS84/CGCS2000 → GCJ-02（与官方偏移一致；中国范围外恒等） */
export function wgs84ToGcj02 (lat, lng) {
  if (outOfChina(lat, lng)) return { lat, lng }
  const d = delta(lat, lng)
  return { lat: lat + d.dLat, lng: lng + d.dLng }
}

/** GCJ-02 → WGS84 一次近似（在偏移点用偏移量回退；误差米级，展示够用） */
export function gcj02ToWgs84 (lat, lng) {
  if (outOfChina(lat, lng)) return { lat, lng }
  const d = delta(lat, lng)
  return { lat: lat - d.dLat, lng: lng - d.dLng }
}

/** GCJ-02 → WGS84 迭代精确（收敛至亚米级；Provider 渲染主路径用这个） */
export function gcj02ToWgs84Exact (lat, lng) {
  if (outOfChina(lat, lng)) return { lat, lng }
  let wgsLat = lat
  let wgsLng = lng
  for (let i = 0; i < 5; i++) {
    const gcj = wgs84ToGcj02(wgsLat, wgsLng)
    // 残差 = 目标 GCJ 点 − 当前 WGS 估计的正向投影，按残差修正估计
    wgsLat += lat - gcj.lat
    wgsLng += lng - gcj.lng
  }
  return { lat: wgsLat, lng: wgsLng }
}
